// Storefront policy links (Privacy / Refund): the ONE place that decides what
// a policy's footer link says and where it goes.
//
// Each store policy is either a link to the distributor's own URL
// (mode "url") or a hosted page on the store (mode "page") at
// /shop/<slug>/privacy-policy or /shop/<slug>/refund-policy. The link text is
// the policy's label, which is also the page title; blank means the default.
//
// SECURITY: URLs are distributor-authored and only ever become an anchor
// `href` after `sanitizeExternalUrl` (http/https). Labels are bound as text.
import { sanitizeExternalUrl } from "@/utils/sanitizeExternalUrl";

export const POLICY_KINDS = ["privacy", "refund"];

export const DEFAULT_POLICY_LABELS = {
  privacy: "Privacy Policy",
  refund: "Refund Policy",
};

/** The in-store route of a hosted policy page. */
export function policyRoutePath(kind, storeSlug) {
  return `/shop/${encodeURIComponent(storeSlug)}/${kind}-policy`;
}

/** The label to show: the store's, or the default text when blank. */
export function resolvePolicyLabel(kind, label) {
  const trimmed = typeof label === "string" ? label.trim() : "";
  return trimmed || DEFAULT_POLICY_LABELS[kind];
}

/**
 * Turn one policy ({label, mode, url, hasPage}) into the link the footer
 * renders, or null when there is nothing to link to.
 *
 * @returns {{label: string, href: string, external: true} |
 *           {label: string, to: string, external: false} | null}
 */
export function resolvePolicyLink(policy, kind, storeSlug) {
  if (!policy) return null;
  const label = resolvePolicyLabel(kind, policy.label);
  if (policy.mode === "page") {
    if (!policy.hasPage || !storeSlug) return null;
    return { label, to: policyRoutePath(kind, storeSlug), external: false };
  }
  const href = sanitizeExternalUrl(policy.url);
  return href ? { label, href, external: true } : null;
}

/** Map the getStorefrontCampaign payload to {privacy, refund} policies. */
export function normalizePolicies(campaign) {
  const c = campaign || {};
  const out = {};
  for (const kind of POLICY_KINDS) {
    out[kind] = {
      url: c[`${kind}_policy_url`] || null,
      mode: c[`${kind}_policy_mode`] === "page" ? "page" : "url",
      label: resolvePolicyLabel(kind, c[`${kind}_policy_label`]),
      hasPage: c[`${kind}_policy_has_page`] === true,
    };
  }
  return out;
}
